/**
 * Register Page Component
 * User registration page with organization creation
 */
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, UserPlus, CheckCircle, Info, Eye, EyeOff } from 'lucide-react';
import apiClient from '@/services/api/client';
import { REGISTRATION_CLOSED_MESSAGE, isRegistrationClosedError } from '@/services/api/registration';

export function RegisterPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password_confirm: '',
    first_name: '',
    last_name: '',
    organization_name: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Validate passwords match
    if (formData.password !== formData.password_confirm) {
      setError('Пароли не совпадают');
      setIsLoading(false);
      return;
    }

    try {
      await apiClient.post('/api/v1/auth/register/', formData);
      setSuccess(true);
    } catch (err: unknown) {
      // KAN-684: регистрация закрыта на сервере (403 registration_closed)
      if (isRegistrationClosedError(err)) {
        setError(REGISTRATION_CLOSED_MESSAGE);
        return;
      }
      const error = err as { response?: { data?: Record<string, string[]> } };
      const data = error.response?.data;
      if (data) {
        // Mapping backend field names to Russian UI labels
        const fieldLabels: Record<string, string> = {
          first_name: 'Имя',
          last_name: 'Фамилия',
          email: 'Email',
          password: 'Пароль',
          password_confirm: 'Подтверждение пароля',
          organization_name: 'Название компании',
          non_field_errors: 'Ошибка',
        };
        // Format validation errors with Russian field names
        const messages = Object.entries(data)
          .map(([field, errors]) => {
            const label = fieldLabels[field] || field;
            return `${label}: ${errors.join(', ')}`;
          })
          .join('\n');
        setError(messages);
      } else {
        setError('Ошибка регистрации. Попробуйте позже.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
          <h2 className="text-2xl font-semibold text-gray-900">Регистрация успешна!</h2>
          <p className="text-gray-600">
            На вашу почту отправлено письмо для подтверждения email. Пожалуйста, проверьте входящие сообщения.
          </p>
          <Button onClick={() => navigate('/login')} className="mt-4">
            Перейти к входу
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-3xl font-bold text-blue-600">SMETA AI</h1>
          <h2 className="mt-6 text-center text-2xl font-semibold text-gray-900">Регистрация</h2>
          <p className="mt-2 text-center text-sm text-gray-600">Создайте аккаунт для работы с платформой</p>
        </div>

        <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
          {error && (
            <Alert variant="destructive">
              <AlertDescription className="whitespace-pre-line">{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <Input
                  id="first_name"
                  name="first_name"
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder=" "
                  className="peer pt-5 pb-2"
                />
                <Label
                  htmlFor="first_name"
                  className="absolute left-3 top-1 text-xs text-gray-500 transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-sm peer-focus:top-1 peer-focus:text-xs">
                  Имя <span className="text-red-500">*</span>
                </Label>
              </div>
              <div className="relative">
                <Input
                  id="last_name"
                  name="last_name"
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder=" "
                  className="peer pt-5 pb-2"
                />
                <Label
                  htmlFor="last_name"
                  className="absolute left-3 top-1 text-xs text-gray-500 transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-sm peer-focus:top-1 peer-focus:text-xs">
                  Фамилия <span className="text-red-500">*</span>
                </Label>
              </div>
            </div>

            <div className="relative">
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder=" "
                className="peer pt-5 pb-2"
              />
              <Label
                htmlFor="email"
                className="absolute left-3 top-1 text-xs text-gray-500 transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-sm peer-focus:top-1 peer-focus:text-xs">
                Email <span className="text-red-500">*</span>
              </Label>
            </div>

            <div className="relative">
              <Input
                id="organization_name"
                name="organization_name"
                type="text"
                value={formData.organization_name}
                onChange={handleChange}
                placeholder=" "
                className="peer pt-5 pb-2"
              />
              <Label
                htmlFor="organization_name"
                className="absolute left-3 top-1 text-xs text-gray-500 transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-sm peer-focus:top-1 peer-focus:text-xs">
                Название компании
              </Label>
            </div>

            {/* Password requirements hint */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium mb-1">Требования к паролю:</p>
                  <ul className="list-disc list-inside space-y-0.5 text-xs text-blue-700">
                    <li>Минимум 8 символов</li>
                    <li>Заглавная буква (A-Z)</li>
                    <li>Строчная буква (a-z)</li>
                    <li>Цифра (0-9)</li>
                    <li>Спецсимвол (!@#$%^&* и др.)</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPasswords ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder=" "
                  className="peer pt-5 pb-2 pr-10"
                />
                <Label
                  htmlFor="password"
                  className="absolute left-3 top-1 text-xs text-gray-500 transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-sm peer-focus:top-1 peer-focus:text-xs">
                  Пароль <span className="text-red-500">*</span>
                </Label>
                <button
                  type="button"
                  onClick={() => setShowPasswords(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                  aria-label={showPasswords ? 'Скрыть пароли' : 'Показать пароли'}>
                  {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="relative">
                <Input
                  id="password_confirm"
                  name="password_confirm"
                  type={showPasswordConfirm ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.password_confirm}
                  onChange={handleChange}
                  placeholder=" "
                  className="peer pt-5 pb-2 pr-10"
                />
                <Label
                  htmlFor="password_confirm"
                  className="absolute left-3 top-1 text-xs text-gray-500 transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-sm peer-focus:top-1 peer-focus:text-xs">
                  Подтверждение <span className="text-red-500">*</span>
                </Label>
                <button
                  type="button"
                  onClick={() => setShowPasswordConfirm(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                  aria-label={showPasswordConfirm ? 'Скрыть пароли' : 'Показать пароли'}>
                  {showPasswordConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            <span className="text-red-500">*</span> — обязательные поля
          </p>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Регистрация...
              </>
            ) : (
              <>
                <UserPlus className="mr-2 h-4 w-4" />
                Зарегистрироваться
              </>
            )}
          </Button>

          <p className="text-center text-sm text-gray-600">
            Уже есть аккаунт?{' '}
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
              Войти
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
